import React, { useState } from 'react';
import { Bell, Mail, MessageSquare, CheckCircle, AlertTriangle } from 'lucide-react';
import { onboardingIntegrationClient } from '../../../api/onboardingIntegrationClient';

interface SimpleNotificationsProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const SimpleNotifications: React.FC<SimpleNotificationsProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [notifications] = useState([
    {
      id: 'notif-1',
      type: 'task_due_soon',
      message: 'Tyler ERP Setup Documentation is due tomorrow',
      channel: 'email',
      status: 'sent',
      timestamp: '2024-08-24T10:30:00Z'
    },
    {
      id: 'notif-2',
      type: 'task_overdue',
      message: 'SSO Configuration task is 2 days overdue',
      channel: 'sms',
      status: 'sent',
      timestamp: '2024-08-24T09:00:00Z'
    }
  ]);

  const [testResult, setTestResult] = useState<string>('');

  const sendTestNotification = async () => {
    try {
      await onboardingIntegrationClient.sendTestNotification({
        ruleId: 'test-rule',
        channels: ['email'],
        recipients: ['client@example.com'],
        message: 'Test notification from Tyler Technologies Launch Pad'
      });
      setTestResult('Test notification sent successfully!');
    } catch (error) {
      setTestResult('Failed to send test notification');
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return <Mail className="w-4 h-4 text-blue-600" />;
      case 'sms': return <MessageSquare className="w-4 h-4 text-green-600" />;
      default: return <Bell className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'sent' 
      ? 'text-[#8A9150] bg-[#8A9150]/10 border-[#8A9150]/20'
      : 'text-red-600 bg-red-100 border-red-200';
  };

  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Bell className="w-5 h-5 text-[#4267A1] mr-2" />
        Automated Notifications
      </h3>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-1">Task Reminders</h4>
            <p className="text-sm text-blue-700">24h before due date</p>
          </div>
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="font-medium text-red-900 mb-1">Overdue Alerts</h4>
            <p className="text-sm text-red-700">Daily at 9 AM</p>
          </div>
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="font-medium text-yellow-900 mb-1">Client Inactive</h4>
            <p className="text-sm text-yellow-700">After 5 days</p>
          </div>
        </div>
        
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Recent Notifications</h4>
          <div className="space-y-2">
            {notifications.map((notification) => (
              <div key={notification.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                {getChannelIcon(notification.channel)}
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{notification.message}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(notification.timestamp).toLocaleString()}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(notification.status)}`}>
                  {notification.status.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {userRole !== 'client' && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={sendTestNotification}
                className="px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 font-medium"
              >
                Send Test Notification
              </button>
              
              {testResult && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-[#8A9150]" />
                  <span className="text-sm text-[#8A9150]">{testResult}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="text-xs text-gray-500">
          <p>• Notifications use existing Twilio (SMS) and SendGrid (email) integration</p>
          <p>• All notification events are logged for audit purposes</p>
          <p>• Clients can manage notification preferences in their profile</p>
        </div>
      </div>
    </div>
  );
};
